export const DEFAULT_LOT_AVAILABLE_DISPLAY_CAP_CT = 10;

export const DEFAULT_INVENTORY_SETTINGS = {
  lots: {
    defaultShapeFilter: '',
    showAvailableWeightToClients: true,
    maxAvailableDisplayCt: DEFAULT_LOT_AVAILABLE_DISPLAY_CAP_CT,
    allowOrderOverStock: false,
  },
  certified: {
    requireShapeFilter: false,
    defaultShapeFilter: '',
    maxCaratRangeSpan: null,
    defaultMinCarat: null,
    defaultMaxCarat: null,
  },
};

export function resolveLotAllowOrderOverStock(lot, lotsSettings) {
  if (lot?.allowOrderOverStock === true) return true;
  if (lot?.allowOrderOverStock === false) return false;
  return lotsSettings?.allowOrderOverStock === true;
}

export function resolveInventorySettings(companyOrSettings) {
  const raw = companyOrSettings?.inventorySettings ?? companyOrSettings ?? {};
  const lots = raw.lots || {};
  const certified = raw.certified || {};

  return {
    lots: {
      defaultShapeFilter: lots.defaultShapeFilter || '',
      showAvailableWeightToClients: lots.showAvailableWeightToClients !== false,
      maxAvailableDisplayCt: Number.isFinite(Number(lots.maxAvailableDisplayCt))
        ? Math.max(0, Number(lots.maxAvailableDisplayCt))
        : DEFAULT_LOT_AVAILABLE_DISPLAY_CAP_CT,
      allowOrderOverStock: lots.allowOrderOverStock === true,
    },
    certified: {
      requireShapeFilter: certified.requireShapeFilter === true,
      defaultShapeFilter: certified.defaultShapeFilter || '',
      maxCaratRangeSpan: certified.maxCaratRangeSpan != null && certified.maxCaratRangeSpan !== ''
        ? Number(certified.maxCaratRangeSpan)
        : null,
      defaultMinCarat: certified.defaultMinCarat != null && certified.defaultMinCarat !== ''
        ? Number(certified.defaultMinCarat)
        : null,
      defaultMaxCarat: certified.defaultMaxCarat != null && certified.defaultMaxCarat !== ''
        ? Number(certified.defaultMaxCarat)
        : null,
    },
  };
}

export function resolveClientDefaultShapeFilter(inventorySettings, segment = 'lots') {
  const settings = resolveInventorySettings(inventorySettings);
  const key = segment === 'certified' ? 'certified' : 'lots';
  const shape = settings[key].defaultShapeFilter;
  return shape ? String(shape).toUpperCase() : '';
}

/** Clients always need a concrete shape when "All" is hidden. */
export function resolveClientShapeSelection(defaultShape, inventoryShapes) {
  const normalizedDefault = defaultShape ? String(defaultShape).toUpperCase() : '';
  if (normalizedDefault) return normalizedDefault;
  const available = (inventoryShapes || [])
    .filter(Boolean)
    .map((s) => String(s).toUpperCase());
  return available[0] || '';
}

export function validateClientCaratRange(minCarat, maxCarat, inventorySettings) {
  const span = inventorySettings?.certified?.maxCaratRangeSpan;
  if (span == null || !Number.isFinite(span)) return { ok: true };

  const min = minCarat != null && minCarat !== '' ? Number(minCarat) : null;
  const max = maxCarat != null && maxCarat !== '' ? Number(maxCarat) : null;
  if (min == null || max == null || !Number.isFinite(min) || !Number.isFinite(max)) {
    return { ok: true };
  }
  if (max - min > span) {
    return { ok: false, errorKey: 'inventory.certified.caratRangeTooWide', span };
  }
  return { ok: true };
}

export function mapInventorySettingsToForm(data) {
  const resolved = resolveInventorySettings(data);
  return {
    inventorySettings: {
      lots: {
        ...resolved.lots,
        maxAvailableDisplayCt: String(resolved.lots.maxAvailableDisplayCt),
      },
      certified: {
        ...resolved.certified,
        maxCaratRangeSpan: resolved.certified.maxCaratRangeSpan != null
          ? String(resolved.certified.maxCaratRangeSpan) : '',
        defaultMinCarat: resolved.certified.defaultMinCarat != null
          ? String(resolved.certified.defaultMinCarat) : '',
        defaultMaxCarat: resolved.certified.defaultMaxCarat != null
          ? String(resolved.certified.defaultMaxCarat) : '',
      },
    },
  };
}

export function buildInventorySavePayload(form) {
  const inv = form.inventorySettings || {};
  const lots = inv.lots || {};
  const certified = inv.certified || {};

  return {
    inventorySettings: {
      lots: {
        defaultShapeFilter: lots.defaultShapeFilter || '',
        showAvailableWeightToClients: lots.showAvailableWeightToClients !== false,
        maxAvailableDisplayCt: Number(lots.maxAvailableDisplayCt) || 0,
        allowOrderOverStock: lots.allowOrderOverStock === true,
      },
      certified: {
        requireShapeFilter: certified.requireShapeFilter === true,
        defaultShapeFilter: certified.defaultShapeFilter || '',
        maxCaratRangeSpan: certified.maxCaratRangeSpan === '' ? null : Number(certified.maxCaratRangeSpan),
        defaultMinCarat: certified.defaultMinCarat === '' ? null : Number(certified.defaultMinCarat),
        defaultMaxCarat: certified.defaultMaxCarat === '' ? null : Number(certified.defaultMaxCarat),
      },
    },
  };
}
